import type { ChildTicketTask } from '@/lib/services/childTicketService'
import type { TaskFormFieldDefinition } from './types'
import { getModule, resolveModuleForField } from './registry'

/** Task-level FE scheduling is disabled until parent ticket FE dispatch is finalized. */
export const TASK_FE_SCHEDULE_ENABLED = false

const TASK_FE_SCHEDULE_MODULES = new Set(['core.fe_schedule', 'core.fe_schedule_ref'])

const FE_VISIT_LEGACY_STATUSES = [
  'fe_visit',
  'fe_visit_pending',
  'fe_visit_accepted',
  'fe_visit_rejected',
  'fe_visit_en_route',
  'fe_visit_arrived',
  'fe_visit_in_progress',
] as const

const ACTION_TO_MODULES: Record<string, TaskFormFieldDefinition[]> = {
  fe_schedule: TASK_FE_SCHEDULE_ENABLED
    ? [
        { key: 'store', module: 'core.store', label: 'Store', required: true },
        { key: 'fe', module: 'core.fe_schedule', label: 'FE schedule', requires: ['store'] },
      ]
    : [
        { key: 'assignee', module: 'core.assignee', label: 'Assignee', required: false },
        { key: 'store', module: 'core.store', label: 'Store', required: true },
      ],
  fe_visit: [{ key: 'fe_visit_tracking', module: 'core.fe_visit_tracking', label: 'FE visit tracking' }],
  field_visit: [{ key: 'fe_visit_tracking', module: 'core.fe_visit_tracking', label: 'FE visit tracking' }],
  assets: [{ key: 'assets', module: 'core.assets', label: 'Equipment' }],
  parts: [{ key: 'parts', module: 'core.parts', label: 'Parts' }],
  parts_procurement: [{ key: 'part_procurement', module: 'core.part_procurement', label: 'Part procurement' }],
  document_collection: [{ key: 'document_collection', module: 'core.document_collection', label: 'Document collection' }],
  work_permit: [{ key: 'permit', module: 'core.work_permit', label: 'Work permit' }],
}

/** True when task has no template snapshot — legacy action_type/status inference may apply. */
export function shouldUseLegacyFormFields(task: ChildTicketTask): boolean {
  if (task.form_fields !== null && task.form_fields !== undefined) {
    return false
  }
  if (task.workflow_template_step_id != null) {
    return false
  }
  return true
}

/** Resolved form schema for a task (template snapshot preferred; legacy fallback for old tasks only). */
export function resolveTaskFormFields(task: ChildTicketTask): TaskFormFieldDefinition[] {
  let fields: TaskFormFieldDefinition[]

  if (!shouldUseLegacyFormFields(task)) {
    fields = (task.form_fields ?? []) as TaskFormFieldDefinition[]
  } else {
    const action = (task.action_type ?? '').trim()
    if (action && action !== 'none' && ACTION_TO_MODULES[action]) {
      fields = ACTION_TO_MODULES[action]
    } else {
      const status = task.status ?? ''
      if (status === 'fe_plotting' || FE_VISIT_LEGACY_STATUSES.includes(status as (typeof FE_VISIT_LEGACY_STATUSES)[number])) {
        fields = ACTION_TO_MODULES.fe_schedule
      } else if (['parts_prep', 'parts_transit'].includes(status)) {
        fields = ACTION_TO_MODULES.parts
      } else if (status === 'permit_prep') {
        fields = ACTION_TO_MODULES.work_permit
      } else if (status === 'cso_review') {
        fields = ACTION_TO_MODULES.assets
      } else {
        fields = []
      }
    }
  }

  return withoutTaskFeScheduleModules(fields)
}

/** True when Details already hosts document uploads (hide Attachments tab). */
export function taskHostsDocumentCollection(task: ChildTicketTask | null | undefined): boolean {
  if (!task) return false
  if ((task.action_type ?? '').trim() === 'document_collection') return true
  return resolveTaskFormFields(task).some((field) => field.module === 'core.document_collection')
}

/** True when Details already hosts FE visit timeline (hide Visit events tab). */
export function taskHostsFeVisitTracking(task: ChildTicketTask | null | undefined): boolean {
  if (!task) return false
  return resolveTaskFormFields(task).some((field) => field.module === 'core.fe_visit_tracking')
}

function withoutTaskFeScheduleModules(fields: TaskFormFieldDefinition[]): TaskFormFieldDefinition[] {
  if (TASK_FE_SCHEDULE_ENABLED) {
    return fields
  }

  return fields.filter((field) => !TASK_FE_SCHEDULE_MODULES.has(field.module))
}

export function taskHasAssigneeField(task: ChildTicketTask): boolean {
  return resolveTaskFormFields(task).some((f) => f.module === 'core.assignee')
}

export function badgesFromFormFields(fields: TaskFormFieldDefinition[]): string[] {
  const badges: string[] = []
  for (const field of fields) {
    const mod = resolveModuleForField(field) ?? getModule(field.module)
    if (mod?.badge) {
      badges.push(mod.badge)
      continue
    }
    if (field.module === 'core.store') badges.push('Store')
    else if (field.module === 'core.fe_schedule' || field.module === 'core.fe_schedule_ref') badges.push('Store · FE')
    else if (field.module === 'core.assets') badges.push('Assets')
    else if (field.module === 'core.parts') badges.push('Parts')
    else if (field.module === 'core.part_procurement') badges.push('Part')
    else if (field.module === 'core.document_collection') badges.push('Documents')
    else if (field.module === 'core.fe_visit_tracking') badges.push('FE Visit')
    else if (field.module === 'core.work_permit') badges.push('Permit')
    else if (field.module === 'core.assignee') badges.push('Assignee')
    else if (field.module.startsWith('embed.') || field.module.startsWith('custom.')) badges.push('Embed')
  }
  return [...new Set(badges)]
}

export function dependenciesMet(
  field: TaskFormFieldDefinition,
  valuesByKey: Record<string, unknown>,
  storeId?: number | null,
  allFields?: TaskFormFieldDefinition[],
): { ok: boolean; message?: string } {
  for (const dep of field.requires ?? []) {
    const depFieldDef = resolveDependencyField(dep, allFields)

    if (depFieldDef?.module === 'core.store' || dep === 'store') {
      if (storeId != null) {
        continue
      }
      return { ok: false, message: 'Link a store before using this field.' }
    }

    const valueKey = depFieldDef?.key ?? dep
    const val = valuesByKey[valueKey]
    if (val === null || val === undefined || val === '') {
      return {
        ok: false,
        message: `Complete "${depFieldDef?.label ?? dep}" first.`,
      }
    }
  }

  if (field.module === 'core.fe_schedule' && storeId == null) {
    return { ok: false, message: 'Link a store on this ticket before scheduling field engineers.' }
  }

  return { ok: true }
}

/** Resolve requires entry to a sibling field (supports semantic alias "store" → core.store). */
function resolveDependencyField(
  dep: string,
  allFields?: TaskFormFieldDefinition[],
): TaskFormFieldDefinition | undefined {
  if (!allFields?.length) {
    return dep === 'store' ? { key: dep, module: 'core.store', label: 'Store' } : undefined
  }

  const byKey = allFields.find((f) => f.key === dep)
  if (byKey) return byKey

  if (dep === 'store') {
    return allFields.find((f) => f.module === 'core.store')
  }

  return undefined
}
